import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { auth, tokens } from "@/api";

/** Restores the session on reload: a stored token still has to be proven. */
export const restoreSession = createAsyncThunk("auth/restore", async () => {
  if (!tokens.access) return null;
  return auth.me();
});

export const signIn = createAsyncThunk(
  "auth/signIn",
  async ({ username, password }, { rejectWithValue }) => {
    try {
      return await auth.login(username, password);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

/** Finishes an SSO sign-in: the proxy sent the browser back with its session cookie. */
export const completeSso = createAsyncThunk("auth/completeSso", async (_, { rejectWithValue }) => {
  try {
    return await auth.completeSso();
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

export const signOut = createAsyncThunk("auth/signOut", async () => {
  const nextUrl = await auth.logout();
  // An SSO sign-out must also end Zitadel's session, or the next SSO click would sign straight back in.
  if (nextUrl) window.location.href = nextUrl;
});

const slice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    // "unknown" until the stored token is checked, so the router can hold
    // off redirecting to /login while that is in flight
    status: "unknown",
    error: null,
  },
  reducers: {
    /** The api client clears tokens on a hard 401; mirror that in state. */
    sessionExpired(state) {
      state.user = null;
      state.status = "anonymous";
    },
    /** The auth proxy sent the browser back with ?error=...: show it on the sign-in screen. */
    ssoFailed(state, action) {
      state.user = null;
      state.status = "anonymous";
      state.error = auth.rememberSsoRefusal(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(restoreSession.pending, (state) => {
        state.status = "loading";
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.user = action.payload;
        state.status = action.payload ? "authenticated" : "anonymous";
      })
      .addCase(restoreSession.rejected, (state) => {
        state.user = null;
        state.status = "anonymous";
      })
      .addCase(signIn.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.user = action.payload;
        state.status = "authenticated";
      })
      .addCase(signIn.rejected, (state, action) => {
        state.status = "anonymous";
        state.error = action.payload || "Sign-in failed";
      })
      .addCase(completeSso.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(completeSso.fulfilled, (state, action) => {
        state.user = action.payload;
        state.status = "authenticated";
      })
      .addCase(completeSso.rejected, (state, action) => {
        state.status = "anonymous";
        state.error = action.payload || "Single sign-on failed";
      })
      .addCase(signOut.fulfilled, (state) => {
        state.user = null;
        state.status = "anonymous";
      });
  },
});

export const { sessionExpired, ssoFailed } = slice.actions;
export default slice.reducer;

export const selectUser = (state) => state.auth.user;
export const selectAuthStatus = (state) => state.auth.status;
export const selectAuthError = (state) => state.auth.error;

/** Mirrors the server's RBAC matrix so the UI can hide what it would reject. */
/**
 * The signed-in role's resource → CRUD grants, delivered with the identity at
 * sign-in. Stored with the user rather than fetched per page: it cannot change
 * between the two calls, and every page that renders a button would otherwise
 * ask for it again.
 */
export const selectPermissions = (state) => state.auth.user?.permissions ?? null;

export const selectCanWrite = (state) => {
  const role = state.auth.user?.role;
  return role === "Site Administrator" || role === "Production Planner";
};
export const selectIsAdmin = (state) =>
  state.auth.user?.role === "Site Administrator";

/**
 * `grants[resource][verb]`, with the role list as the fallback.
 *
 * The grants ride with the identity from /api/login and /api/me, so they are
 * in the store before the first render — but a token issued before they were
 * added carries none, and answering "no" there would lock a legitimate user
 * out of their own screens. The named roles are therefore what the resource
 * meant when it was hardcoded: the answer while grants are absent, never a
 * veto over them. Once grants arrive they are the whole answer, so an edit in
 * Configuration → Access control shows up in the UI on the next sign-in.
 */
const granted = (state, resource, verb, roles) => {
  const role = state.auth.user?.role;
  if (!role) return false;
  const grants = selectPermissions(state);
  if (!grants) return roles.includes(role);
  return !!grants[resource]?.[verb];
};

/**
 * Execution data is the Production Supervisor's job — they may pull, clear
 * and record actuals even though every other write is closed to them.
 *
 * `update` is the general "may touch this feed" answer the page's buttons
 * hang off; the create/delete verbs below separate adding a record from
 * discarding the feed, which are not the same permission.
 */
const EXECUTION_ROLES = [
  "Site Administrator",
  "Production Planner",
  "Production Supervisor",
  "Line Manager",
];
export const selectCanEditExecution = (state) =>
  granted(state, "execution", "update", EXECUTION_ROLES);

/** Recording a new actual — the floor's own write. */
export const selectCanRecordExecution = (state) =>
  granted(state, "execution", "create", EXECUTION_ROLES);

/**
 * Clearing or re-pulling the feed: destructive, and deliberately NOT the
 * permission that records a step. A supervisor entering actuals should not
 * be one mis-click from discarding the month's record.
 */
export const selectCanClearExecution = (state) =>
  granted(state, "execution", "delete", ["Site Administrator"]);

/**
 * Signing off a variance investigation is quality's job, and QA Reviewer's
 * only write — see QA_ALLOWED in core_auth/users.py.
 *
 * Deliberately disjoint from selectCanEditExecution rather than a superset of
 * it: the point of the review gate is that whoever investigated does not also
 * approve, so a planner sees the investigation editor and an approver sees the
 * verdict. The administrator keeps both, because a site with no separate
 * reviewer would otherwise deadlock — and the server records that exemption
 * in the audit trail when it is used.
 */
// All three QA tiers, matching the server (variance/workflow.py QA_ROLES) —
// the Manager and Head could always approve via the API; hiding the buttons
// from them was a frontend-only inconsistency.
const QA_ROLES = [
  "Site Administrator",
  "QA Reviewer",
  "QA Manager",
  "QA Head",
];
// Also not grid-driven: the `variance_verdict` resource opens
// /api/variance/transition, which the supervisor needs for the transitions
// they DO own (raise, investigate, submit). The approve/reject/close steps
// are gated per transition by QA_ROLES in variance/workflow.py, and no
// resource expresses that narrower rule — so mirror the server's list.
export const selectCanApproveVariance = (state) =>
  QA_ROLES.includes(state.auth.user?.role);

/**
 * Releasing a QA hold is quality's decision — all three QA tiers may sign
 * it, plus the administrator. Placing a hold follows selectCanEditExecution
 * (recording that the batch stopped is execution data); releasing it is the
 * quality verdict, which is why the two selectors deliberately differ —
 * mirrors QA_ALLOWED and the role gate in reconciliation/holds.py.
 */
// Deliberately NOT grid-driven, and not `variance_verdict` either: that
// resource is only /api/variance/transition, which the supervisor and line
// manager also hold — reading it here would put quality's release button on
// the floor's screen. Release is gated server-side by QA_ALLOWED
// (reconciliation/holds.py) with no resource of its own, so the role list IS
// the rule rather than a fallback for one. Give it a resource in
// core_auth/resources.py before making it editable.
export const selectCanReleaseHold = (state) =>
  QA_ROLES.includes(state.auth.user?.role);
